import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Utils
import clsx from 'clsx';
import { matchSorter } from 'match-sorter';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import PropTypes from 'prop-types';

// Components
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Input from '@/components/base/Input';
import Loader from '@/components/base/Loader';

const SearchBox = ({
    onSetValues = null,
    mutateList,
    errorList,
    list,
    searchKeys = [],
    id = '',
    children,
    errors,
    isLoading,
    disabled,
    ...restProps
}) => {
    const [formState, setFormState] = useState({ isSubmitting: false, error: null });
    //lift up this state to unable the check of only filtered options
    const [choices, setChoices] = useState([]);

    const nbChoiceSelected = choices?.filter((choice) => choice?.selected)?.length ?? 0;
    const isUnCheckAll = nbChoiceSelected === choices?.length;

    const onCheckValue = (id_value) => {
        const newList = choices?.map((item) => {
            if (item.id === id_value) {
                return {
                    ...item,
                    selected: !item.selected
                };
            }
            return item;
        });
        onSetValues(newList);
    };

    const checkUncheckAll = () => {
        const newList = choices?.map((item) => {
            return {
                ...item,
                selected: !isUnCheckAll
            };
        });
        onSetValues(newList);
    };

    return (
        <div>
            <SearchBox.Wrapper errors={errors}>
                {React.Children.map(children, (child) => {
                    const newChild = React.cloneElement(child, {
                        formState,
                        setFormState,
                        errorList,
                        checkUncheckAll,
                        onCheckValue,
                        searchKeys,
                        isUnCheckAll,
                        id,
                        mutateList,
                        choices,
                        setChoices,
                        list,
                        isLoading,
                        disabled,
                        ...restProps
                    });
                    return newChild;
                })}
            </SearchBox.Wrapper>
            {errors && <div className="mt-1 w-full font-GMedium text-xs text-icoRed">{errors}</div>}
        </div>
    );
};

SearchBox.propTypes = {
    onSetChoice: PropTypes.func,
    mutateList: PropTypes.func,
    errorList: PropTypes.object,
    errors: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

const Wrapper = ({ errors, ...restProps }) => (
    <div
        className={clsx(
            'rounded-md border border-icoGray-300 px-1 py-5 dark:border-transparent dark:bg-icoDarkMode-deepBlack sm:px-3 sm:py-8',
            { 'border border-icoRed dark:border-icoRed': errors }
        )}
        {...restProps}
    />
);

SearchBox.Wrapper = Wrapper;

const ErrorInForm = ({ formState, setFormState }) => {
    const { t } = useTranslation();

    if (!formState.error) return null;

    return (
        <div className="mb-4 pl-2 pr-4 sm:px-5">
            <InfoBanner
                title={t('errors:error')}
                message={formState.error}
                onClose={() => setFormState((prevState) => ({ ...prevState, error: null }))}
            />
        </div>
    );
};

SearchBox.ErrorInForm = ErrorInForm;

const ErrorInList = ({ errorList, mutateList }) => {
    const { t } = useTranslation();

    if (!errorList) return null;
    return (
        <div className="relative flex h-56 flex-col items-center justify-center space-y-2">
            <p>{t('errors:error_occured_fetch_data')}</p>
            <button
                className="text-icoBlue underline"
                type="button"
                onClick={() => mutateList(null)}>
                {t('try_again')}
            </button>
        </div>
    );
};

SearchBox.ErrorInList = ErrorInList;

const ContentWrapper = ({
    list,
    checkUncheckAll,
    onCheckValue,
    isUnCheckAll,
    formState,
    searchKeys,
    id,
    setFilteredList,
    isLoading,
    choices,
    setChoices,
    disabled,
    children
}) => {
    return React.Children.map(children, (child) => {
        const newChild = React.cloneElement(child, {
            list,
            checkUncheckAll,
            onCheckValue,
            isUnCheckAll,
            formState,
            searchKeys,
            setChoices,
            choices,
            setFilteredList,
            id,
            isLoading,
            disabled
        });
        return newChild;
    });
};

SearchBox.ContentWrapper = ContentWrapper;

const SearchBoxInput = ({
    checkUncheckAll,
    setChoices,
    placeholder,
    list,
    isSubmitting,
    searchKeys = [],
    isUnCheckAll,
    disabled,
    id
}) => {
    const { t } = useTranslation('commons');
    const [searchValue, setSearchValue] = useState('');

    const getChoices = useCallback(
        (filter) => {
            if (!filter) {
                return list;
            }
            return matchSorter(list, filter, {
                keys: searchKeys
            });
        },
        [list, searchKeys]
    );

    const allItems = useMemo(() => getChoices(searchValue), [searchValue, getChoices]);

    useEffect(() => {
        setChoices(allItems);
    }, [allItems]);

    return (
        <div className="pl-2 pr-4 sm:px-5">
            <Input
                id={`search-${id}`}
                inputStyle="search"
                disabled={disabled}
                noBorder={true}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={placeholder ?? null}
                beforeInput={<Icon icon="search" />}
                afterInput={
                    allItems?.length ? (
                        <button
                            type="button"
                            disabled={isSubmitting || disabled}
                            onClick={checkUncheckAll}
                            className="hidden font-GSemiBold text-xs text-icoBlue sm:block">
                            {isUnCheckAll
                                ? t('reports:create.unselect_all')
                                : t('reports:create.select_all')}
                        </button>
                    ) : null
                }
            />
        </div>
    );
};

SearchBox.Input = SearchBoxInput;

const SearchBoxFilteredItems = ({
    checkUncheckAll,
    isUnCheckAll,
    choices,
    id,
    children,
    isSubmitting,
    disabled,
    isLoading,
    noChoicesMessage = '',
    onCheckValue: checkValue
}) => {
    const { t } = useTranslation('commons');

    if (isLoading) {
        return (
            <div className="scrollbar flex h-40 flex-col items-center justify-center space-y-4 pl-2 pr-4 sm:mt-5 sm:px-5">
                <Loader className="h-5 w-5 text-icoBlue" />
            </div>
        );
    }

    if (!choices?.length) {
        return (
            <div className="scrollbar flex max-h-56 flex-col space-y-4 pl-2 pr-4 sm:mt-5 sm:px-5">
                <div className="my-5 text-center text-icoGray-500">
                    {noChoicesMessage ? noChoicesMessage : t('no_options_to_choose_from')}
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="py-m pl-2 pr-4 text-right sm:hidden">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={checkUncheckAll}
                    className="font-GSemiBold text-xs text-icoBlue">
                    {isUnCheckAll ? t('unselect_all') : t('select_all')}
                </button>
            </div>
            <div className="scrollbar flex max-h-56 flex-col space-y-4 py-1 pl-2 pr-4 sm:mt-5 sm:px-5">
                {children({
                    isSubmitting,
                    disabled,
                    checkValue,
                    choices,
                    id
                })}
            </div>
        </div>
    );
};

SearchBox.ListsItems = SearchBoxFilteredItems;

export default SearchBox;
